import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import InputMask from 'react-input-mask'

import { determinePhoneType, getPhoneForServer } from 'utils/phone'

import DropdownMenu from 'components/molecules/DropdownMenu'
import CountryFlag from 'components/atoms/CountryFlag'

import { CountryCode, CountryLength, CountryMask, CountryName, CountryPrefix } from 'types/phone'

import classes from './style.module.css'

export interface InputControlProps {
  id: string
  value: string
  isValid?: boolean | undefined
  onChangeHandler(value: string, id?: string): void
  className?: string
  label?: string | ReactNode
  invalidMessage?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
}

const countries = [CountryCode.RU, CountryCode.KZ, CountryCode.BY, CountryCode.KG, CountryCode.UA, CountryCode.UZ, CountryCode.MD]

export const InputPhone: React.FC<InputControlProps> = ({
  id,
  value,
  isValid,
  onChangeHandler,
  className = '',
  label,
  invalidMessage = '',
  required = false,
  disabled = false,
  placeholder = '',
}) => {
  const [currentCountry, setCurrentCountry] = useState(CountryCode.RU)

  const inputRef: any = useRef(null)

  useEffect(() => {
    const phone = determinePhoneType(value)

    if (phone.code) setCurrentCountry(phone.code)
  }, [value])

  const onChangeCountry = useCallback(
    (country: CountryCode) => {
      setCurrentCountry(country)
      onChangeHandler('', id)
      if (inputRef.current) {
        inputRef.current.getInputDOMNode().focus()
      }
    },
    [id, onChangeHandler],
  )

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = getPhoneForServer(e.target.value)
      if (val.match(/^7[1-58-9]$/)) setCurrentCountry(CountryCode.RU)
      if (val.match(/^7[67]$/)) setCurrentCountry(CountryCode.KZ)

      onChangeHandler(getPhoneForServer(e.target.value), id)
    },
    [onChangeHandler, id],
  )

  const onPaste = useCallback(
    (event: any) => {
      event.preventDefault()

      const { clipboardData } = event
      const pastedText = clipboardData.getData('text')

      const isFromPaste = true
      const phone = determinePhoneType(pastedText, isFromPaste)

      if (phone.code) {
        setCurrentCountry(phone.code)
        onChangeHandler(phone.value, event.target.id)
      } else {
        const pureString = getPhoneForServer(`${value || CountryPrefix[currentCountry]}${phone.value} `).slice(0, CountryLength[currentCountry])
        onChangeHandler(pureString, event.target.id)
      }
    },
    [currentCountry, onChangeHandler, value],
  )

  const onBlur = useCallback(
    (event) => {
      const val = event.target.value

      if (Object.values(CountryPrefix).includes(val.replace(/[^0-9+]/g, ''))) onChangeHandler('', id)
    },
    [id, onChangeHandler],
  )

  // eslint-disable-next-line no-nested-ternary
  const validClass = isValid === false ? 'is-invalid' : isValid === true ? 'is-valid' : ''

  const name = useMemo(() => uuidv4(), [])

  const list = useMemo(
    () =>
      countries.map((country, countryId) => ({
        id: countryId,
        node: (
          <div>
            <span>
              <CountryFlag code={country} />
            </span>
            <span className={classes.countryName}>{CountryName[country]}</span>
            <span className={classes.countryPrefix}>{CountryPrefix[country]}</span>
          </div>
        ),
        disabled: currentCountry === country,
        onClick: () => onChangeCountry(country),
        data: country,
      })),
    [currentCountry, onChangeCountry],
  )

  return (
    <Form.Group className="m-0">
      {label ? <Form.Label className="d-block">{label}</Form.Label> : null}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus */}
      {!disabled ? (
        <DropdownMenu
          toggleText={
            <div className={classes.icon}>
              <CountryFlag code={currentCountry} />
            </div>
          }
          classNameForToggle={classes.dropdownToggle}
          classNameForMenu={classNames(classes.dropdownMenu, className)}
          classNameForMenuItem={classes.dropdownItem}
          toggleVariant="light"
          list={list}
          onClickItem={(item) => setCurrentCountry(item.data)}
        />
      ) : null}

      <InputMask
        id={id}
        className={classNames('inputMask form-control tel', validClass, classes.input, className)}
        value={value || ''}
        mask={CountryMask[currentCountry]}
        maskChar={null}
        formatChars={{ '*': '[0-9]' }}
        onChange={onChange}
        onPaste={onPaste}
        onBlur={onBlur}
        required={required}
        autoComplete="none"
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        ref={inputRef}
        alwaysShowMask={!disabled}
      />
      {invalidMessage && isValid === false ? <Form.Control.Feedback type="invalid">{invalidMessage}</Form.Control.Feedback> : null}
    </Form.Group>
  )
}

export default InputPhone
